<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div class="relative z-20" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-if="visible" @keyup.esc="hide">
        <!--
          Background backdrop, show/hide based on modal state.

          Entering: "ease-out duration-300"
            From: "opacity-0"
            To: "opacity-100"
          Leaving: "ease-in duration-200"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div class="fixed z-20 inset-0 overflow-y-auto overflow-x-scroll">
            <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                <!--
                  Modal panel, show/hide based on modal state.

                  Entering: "ease-out duration-300"
                    From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    To: "opacity-100 translate-y-0 sm:scale-100"
                  Leaving: "ease-in duration-200"
                    From: "opacity-100 translate-y-0 sm:scale-100"
                    To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                -->
                <div
                    class="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:w-full sm:p-6"
                    :class="{'lg:w-8/12 xl:w-7/12 md:w-5/5 sm:w-11/12 xs:w-full' : !showComparison, 'lg:w-11/12 xl:w-4/5': showComparison}">
                    <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                        <button type="button" class="btn-close" @click="cancel">
                            <span class="sr-only">{{ $t('shared.modals.close') }}</span>
                            <icon name="close"/>
                        </button>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="!showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <form ref="editSettingsForm">
                                <h2 class="text-lg leading-6 font-bold text-gray-900">{{title}}</h2>
                                <table class="table-default mt-5 mb-3">
                                    <thead>
                                        <tr>
                                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show') }}</th>
                                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show_start') }}</th>
                                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.price_drop') }}</th>
                                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.show_end') }}</th>
                                            <th scope="col">{{ $t('pages.settings.edit_dialog.columns.default') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200 bg-white">
                                        <tr class="hover:bg-gray-100">
                                            <td>
                                                <input
                                                    :id="'priceDrop'"
                                                    :name="'priceDrop'"
                                                    v-model="form.show"
                                                    @change="updateTimeRanges()"
                                                    class="mr-1"
                                                    type="checkbox">
                                            </td>
                                            <td>
                                                <vue-timepicker
                                                    :ref="'showStartTime'"
                                                    v-if="form.show"
                                                    v-model="form.showStartTime"
                                                    :format="$t('locale.formatTimeTwelveHours')"
                                                    @input="updateTimeRanges()"
                                                    @open="updateTimeRanges()"
                                                    @focus="updateTimeRanges()"
                                                    @change="updateTimeRanges()"
                                                    @close="updateTimeRanges()"
                                                    @blur="updateTimeRanges()"
                                                    :hour-range="hourRanges['showStartTime']"
                                                    :minute-range="minuteRanges['showStartTime']"
                                                    fixed-dropdown-button
                                                    hide-clear-button
                                                    auto-scroll
                                                    advanced-keyboard
                                                    manual-input/>
                                            </td>
                                            <td>
                                                <vue-timepicker
                                                    :ref="'priceDropTime'"
                                                    v-model="form.priceDropTime"
                                                    :format="$t('locale.formatTimeTwelveHours')"
                                                    @input="updateTimeRanges()"
                                                    @open="updateTimeRanges()"
                                                    @focus="updateTimeRanges()"
                                                    @change="updateTimeRanges()"
                                                    @close="updateTimeRanges()"
                                                    @blur="updateTimeRanges()"
                                                    :hour-range="hourRanges['priceDropTime']"
                                                    :minute-range="minuteRanges['priceDropTime']"
                                                    fixed-dropdown-button
                                                    hide-clear-button
                                                    auto-scroll
                                                    advanced-keyboard
                                                    manual-input/>
                                            </td>
                                            <td>
                                                <vue-timepicker
                                                    :ref="'showEndTime'"
                                                    v-if="form.show"
                                                    v-model="form.showEndTime"
                                                    :format="$t('locale.formatTimeTwelveHours')"
                                                    @input="updateTimeRanges()"
                                                    @open="updateTimeRanges()"
                                                    @focus="updateTimeRanges()"
                                                    @change="updateTimeRanges()"
                                                    @close="updateTimeRanges()"
                                                    @blur="updateTimeRanges()"
                                                    :hour-range="hourRanges['showEndTime']"
                                                    :minute-range="minuteRanges['showEndTime']"
                                                    fixed-dropdown-button
                                                    hide-clear-button
                                                    auto-scroll
                                                    advanced-keyboard
                                                    manual-input/>
                                            </td>
                                            <td class="hover:bg-gray-200 cursor-pointer">
                                                <icon v-if="isDefault" name="badge-check" class="w-6 h6 alert-icon-info"/>
                                                <icon v-if="!isDefault" name="x" class="w-6 h6 alert-icon-error"/>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table class="table-default mt-5 mb-3">
                                    <thead>
                                        <tr>
                                            <th scope="col">{{ $t('shared.modals.adjust_price_drop_modal.form.no_levels.label') }}</th>
                                            <th scope="col">{{ $t('shared.modals.adjust_price_drop_modal.form.price.label') }}</th>
                                            <th scope="col">{{ $t('shared.modals.adjust_price_drop_modal.form.quantity.label') }}</th>
                                            <th scope="col">{{ $t('shared.modals.adjust_price_drop_modal.form.factor.label') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200 bg-white">
                                        <tr class="hover:bg-gray-100">
                                            <td class="relative">
                                                <input
                                                    ref="numberOfLevels"
                                                    type="number"
                                                    name="numberOfLevels"
                                                    id="numberOfLevels"
                                                    class="w-1/2" :class="{'error' : numberOfLevelsHasError(), 'warning' : numberOfLevelsHasWarning()}"
                                                    :placeholder="form.numberOfLevels"
                                                    v-model="form.numberOfLevels"
                                                    :min="minNumberOfLevels"
                                                        >
                                                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" v-if="isNumberOfLevelsValid()">
                                                    <icon class="h-5 w-5"
                                                          :class="{'text-red-500' : numberOfLevelsHasError(), 'text-yellow-500' : numberOfLevelsHasWarning() }"
                                                          name="exclamation"
                                                    />
                                                </div>
                                            </td>
                                            <td class="relative">
                                                <input
                                                    ref="price"
                                                    type="number"
                                                    name="price"
                                                    id="price"
                                                    class="w-1/2" :class="{'error' : priceHasError(), 'warning' : priceHasWarning()}"
                                                    :placeholder="form.price"
                                                    v-model="form.price"
                                                    :min="0"
                                                >
                                                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" v-if="isPriceValid()">
                                                    <icon class="h-5 w-5"
                                                          :class="{'text-red-500' : priceHasError(), 'text-yellow-500' : priceHasWarning() }"
                                                          name="exclamation"
                                                    />
                                                </div>
                                            </td>
                                            <td class="relative">
                                                <input
                                                    ref="quantity"
                                                    type="number"
                                                    name="quantity"
                                                    id="quantity"
                                                    class="w-1/2" :class="{'error' : quantityHasError(), 'warning' : quantityHasWarning() }"
                                                    :placeholder="form.quantity"
                                                    v-model="form.quantity"
                                                    :min="minQuantity"
                                                >
                                                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" v-if="isQuantityValid()">
                                                    <icon class="h-5 w-5"
                                                          :class="{'text-red-500' : quantityHasError(), 'text-yellow-500' : quantityHasWarning() }"
                                                          name="exclamation"
                                                    />
                                                </div>
                                            </td>
                                            <td class="relative">
                                                <input
                                                    ref="factor"
                                                    type="number"
                                                    step="0.01"
                                                    name="factor"
                                                    id="factor"
                                                    class="w-1/2" :class="{'error' : factorHasError(), 'warning' : factorHasWarning()}"
                                                    :placeholder="form.factor"
                                                    v-model="form.factor"
                                                    :min="minFactor"
                                                >
                                                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" v-if="isFactorValid()">
                                                    <icon class="h-5 w-5"
                                                          :class="{'text-red-500' : factorHasError(), 'text-yellow-500' : factorHasWarning() }"
                                                          name="exclamation"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </form>
                            <alert-message
                                v-if="errors && errors.length > 0"
                                :error-type="ERROR_TYPE_ERROR"
                                :messages="errors"
                                :show-as-list="false"
                                :display-errors-title="false"
                                class="mb-5"
                            />
                            <alert-message
                                v-if="warnings && warnings.length > 0"
                                :error-type="ERROR_TYPE_WARNING"
                                :messages="warnings"
                                :show-as-list="false"
                                :display-errors-title="false"
                                class="mb-5"
                            />
                        </div>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h2 class="text-lg leading-6 font-bold text-gray-900">{{$t('shared.modals.adjust_price_drop_modal.title')}}</h2>
                            <div class="mt-10 w-full flex">
                                <div ref="titleBefore" class="pr-5 opacity-50">
                                    <h3 class="text-xl leading-6 font-medium">{{$t('shared.modals.adjust_price_drop_modal.adjust_overview.before_adjustment')}}</h3>
                                </div>
                                <div>
                                    <h3 class="text-xl leading-6 bold">{{$t('shared.modals.adjust_price_drop_modal.adjust_overview.after_adjustment')}}</h3>
                                </div>
                            </div>
                            <div class="w-full">
                                <pyramid-levels-before-and-after
                                    :levels="this.filteredLevels"
                                    :hide-status="true"
                                    :forceHeaderSecondLine="true"
                                    ref="beforeAndAfter"
                                    @updated="resizeHandler"
                                />
                            </div>
                        </div>
                    </div>

                    <alert-message
                        v-if="Array.isArray(updateErrors) && updateErrors.length > 0 || updateErrors !== null && this.updateErrors.constructor === Object"
                        :errorType="ERROR_TYPE_ERROR"
                        :messages="updateErrors"
                        :show-as-list="false"
                        :display-errors-title="false"
                        class="mb-5"
                    />
                    <warning class="mt-10 text-yellow-500 bg-orange-100" v-if="showComparison && isAdjustmentNeeded()">
                        <b>{{$t('all.note')}}</b>
                        {{$t('shared.modals.adjust_price_drop_modal.warning_adjust')}}
                    </warning>
                    <warning class="mt-10 text-yellow-500 bg-orange-100" v-if="quantityTooHigh">
                        <div>
                            <div>{{$t('shared.modals.adjust_price_drop_modal.warnings.1')}}</div>
                            <div class="mt-4">
                                <b>{{$t('all.note')}}</b>
                                {{$t('shared.modals.adjust_price_drop_modal.warnings.note_text')}}
                            </div>
                        </div>
                    </warning>
                    <div class="mt-5 sm:mt-4 flex flex-row-reverse">
                        <button v-if="showComparison" type="button" class="btn btn-primary ml-5" @click="confirm">{{$t('shared.modals.confirm')}}</button>
                        <button v-if="!showComparison" type="button" class="btn btn-primary ml-5" @click="preview">{{$t('shared.modals.preview')}}</button>
                        <button v-if="showComparison" type="button" class="btn btn-secondary ml-5" @click="adjust">{{$t('shared.modals.adjust')}}</button>
                        <button type="button" class="btn btn-secondary" @click="cancel">{{$t('shared.modals.cancel')}}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import {ErrorTypes} from '../../mixins/ErrorTypes';
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import Warning from "../Alerts/Warning";
import QuantityAdjustmentOverview from "../Overviews/QuantityAdjustmentOverview";
import PyramidLevelsBeforeAndAfter from "../Tables/PyramidLevelsBeforeAndAfter";
import moment from "moment/moment";
import VueTimepicker from 'vue2-timepicker';
import TimeZoneService from "../../Services/TimeZoneService";

export default {
    layout: Layout,
    components: {
        Icon,
        AlertMessage,
        QuantityAdjustmentOverview,
        PyramidLevelsBeforeAndAfter,
        Warning,
        VueTimepicker
    },
    mixins:[
        ErrorTypes
    ],
    props: {
        auction: Object,

        levelPhase: {
            type: Number,
            default: 0
        },
        adjustedLevel: Object,

        currentNumberOfLevels: { type: Number, default: 0 },
        minNumberOfLevels: { type: Number, default: 1 },

        currentPrice: { type: Number, default: 0 },

        currentQuantity: { type: Number, default: 0 },
        minQuantity: { type: Number, default: 0 },
        maxQuantity: { type: Number, default: 0 },

        currentFactor: { type: String, default: '0.00' },
        minFactor: { type: String, default: '0.00' },
        maxFactor: { type: String, default: '10.00' }, // TODO: to be refined

        minTime: Object,
        maxTime: Object,
        showEnabled: { type: Boolean, default: false },
        priceDropTime: Object,
        showStart: Object,
        showEnd: Object,

        title: String,
        offsetInSeconds:  { type: Number, default: 120 },
        priceDropTimeSettings: Array,
        liveShowTimeSettings: Array,
    },
    data() {
        return {
            visible: false,
            errors: {
                type: Array,
                default: []
            },
            warnings: {
                type: Array,
                default: []
            },
            showComparison: false,
            quantityTooHigh: false,

            newLevels: [],
            oldLevels: [],
            updateErrors: {
                type: Array,
                default: []
            },

            form: {
                numberOfLevels: 0,
                price: 0,
                quantity: 0,
                factor: 0,
                show: true,
                priceDropTime: this.minTime,
                showStartTime: this.minTime,
                showEndTime: this.maxTime,
                campaignId: -1
            },
            hourRanges: {
                priceDropTime: [],
                showStartTime: [],
                showEndTime: [],
            },
            minuteRanges: {
                priceDropTime: [],
                showStartTime: [],
                showEndTime: [],
            },
            isDefault: true
        }
    },
    methods: {
        resizeHandler() {
            if (this.$refs.titleBefore) {
                if (this.$refs.beforeAndAfter && this.$refs.beforeAndAfter) {
                    let dimensions = this.$refs.beforeAndAfter.getDimensions();
                    this.$refs.titleBefore.style.width = ( dimensions.left + dimensions.mid ) + 'px';
                }
            }
        },
        show() {
            this.visible = true;
            this.form.numberOfLevels = this.currentNumberOfLevels;
            this.form.price = this.currentPrice;
            this.form.quantity = this.currentQuantity;
            this.form.factor = this.currentFactor;
            this.form.priceDropTime = this.toTimeString(this.priceDropTime);
            this.form.showStartTime = this.toTimeString(this.showStart);
            this.form.showEndTime = this.toTimeString(this.showEnd);
            this.form.show = this.showEnabled;
            this.updateTimeRanges();
            let campaign = this.getCampaignByTime(this.priceDropTime);
            this.form.campaignId = campaign ? campaign.id : -1;
            this.isDefault = this.areSettingsDefault();
            this.updateErrors = [];
            this.$nextTick(() => this.$refs.numberOfLevels.focus());
        },
        hide() {
            this.errors = [];
            this.warnings = [];
            this.showComparison = false;
            this.quantityTooHigh = false;
            this.visible = false;
        },
        cancel() {
            this.hide();
            this.$emit('cancel');
            this.showComparison = false;
        },
        adjust() {
            if (true === this.showComparison) {
                this.showComparison = false;
                this.$nextTick(() => this.$refs.numberOfLevels.focus());
            }
        },
        preview() {
            if (this.checkForm()) {
                let app = this;
                this.form.newNumberOfLevels = parseInt(this.$refs.numberOfLevels.value);
                this.form.newPriceDropPrice = parseFloat(this.$refs.price.value);
                this.form.newQuantity = parseInt(this.$refs.quantity.value);
                this.form.newFactor = parseFloat(this.$refs.factor.value);
                this.updateErrors = [];

                axios.post(route('auctions.adjust.phase', { auction: this.auction.id, index: this.levelPhase, confirmed: 0 }), this.getFormData())
                    .then((response) => {
                    app.oldLevels = response.data.data.preview.old;
                    app.newLevels = response.data.data.preview.new;
                    app.showComparison = true;
                })
                .catch((error) => {
                    if (error.response.status === 422) {
                        this.updateErrors = error.response.data.errors;
                    } else {
                        let errors = error?.response?.data?.errors;
                        if (Array.isArray(errors)) {
                            this.updateErrors = errors;
                        } else if (typeof errors === 'string') {
                            this.updateErrors = [this.$t('all.server_error'), errors];
                        } else {
                            this.updateErrors = [this.$t('all.server_error')];
                        }
                    }
                    console.error(error);
                });
            }
        },
        confirm() {
            let app = this;
            this.updateErrors = [];
            axios.post(route('auctions.adjust.phase', { auction: this.auction.id, index: this.levelPhase, confirmed: 1 }), this.getFormData())
            .then((response) => {
                app.$emit('accept');
                app.showComparison = false;
                app.hide();
            })
            .catch((error) => {
                if (error.response.status === 422) {
                    this.updateErrors = error.response.data.errors;
                } else {
                    let errors = error?.response?.data?.errors;
                    if (Array.isArray(errors)) {
                        this.updateErrors = errors;
                    } else if (typeof errors === 'string') {
                        this.updateErrors = [this.$t('all.server_error'), errors];
                    } else {
                        this.updateErrors = [this.$t('all.server_error')];
                    }
                }
                console.error(error);
            });
        },
        getFormData() {
           return {
               numberOfLevels: this.form.newNumberOfLevels,
               price: this.form.newPriceDropPrice,
               quantity: this.form.newQuantity,
               factor: this.form.newFactor,
               priceDropTime: this.form.priceDropTime,
               show: this.form.show,
               showStartTime: this.form.showStartTime,
               showEndTime: this.form.showEndTime,
               editedLevelId: this.adjustedLevel.id,
               editedCampaignId: this.form.campaignId,
           }
        },
        checkForm: function () {
            this.errors = [];
            this.warnings = [];

            let numberOfLevelsCheck = this.checkNumberOfLevels()
            this.errors.push(...numberOfLevelsCheck.errors);
            this.warnings.push(...numberOfLevelsCheck.warnings);

            let priceCheck = this.checkPrice()
            this.errors.push(...priceCheck.errors);
            this.warnings.push(...priceCheck.warnings);

            let quantityCheck = this.checkQuantity()
            this.errors.push(...quantityCheck.errors);
            this.warnings.push(...quantityCheck.warnings);

            let factorCheck = this.checkFactor()
            this.errors.push(...factorCheck.errors);
            this.warnings.push(...factorCheck.warnings);

            let max = moment(this.getMaxTime(), this.$t('locale.formatTimeTwelveHours'));
            let pd = moment.tz(this.form.priceDropTime, this.$t('locale.formatTimeTwelveHours'), this.tz.auctionTimeZone());
            let priceDropTime = moment(max);
            priceDropTime.hours(pd.hours()).minutes(pd.minutes()).seconds(pd.seconds());

            if (this.getMinTime().valueOf() > priceDropTime.valueOf()) {
                this.errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.price_drop_time.too_early', {time: this.toTimeString(this.getMinTime())}));
            }
            if(this.getMaxTime().valueOf() < priceDropTime.valueOf()) {
                this.errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.price_drop_time.too_late', {time: this.toTimeString(this.getMaxTime())}));
            }
            return this.errors.length === 0 && this.warnings.length === 0;
        },
        checkNumberOfLevels() {
            let errors = [];
            let warnings = [];
            if (this.$refs.numberOfLevels) {
                let numberOfLevels = this.$refs.numberOfLevels.value;

                if (numberOfLevels.length < 1) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.no_levels.required'));
                }
                if (numberOfLevels.length > 0 && isNaN(parseInt(numberOfLevels))) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.no_levels.number'));
                }
                if (parseInt(numberOfLevels) < this.minNumberOfLevels) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.no_levels.too_low', {min: this.minNumberOfLevels}));
                }
            }
            return {errors: errors, warnings: warnings};
        },
        numberOfLevelsHasError() {
            return this.checkNumberOfLevels().errors.length > 0;
        },
        numberOfLevelsHasWarning() {
            return this.checkNumberOfLevels().warnings.length > 0;
        },
        isNumberOfLevelsValid() {
            return this.numberOfLevelsHasError() || this.numberOfLevelsHasWarning();
        },
        checkPrice() {
            let errors = [];
            let warnings = [];
            if (this.$refs.price) {
                let price = this.$refs.price.value;

                if (price.length < 1) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.price.required'));
                }
                if (price.length > 0 && isNaN(parseInt(price))) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.price.number'));
                }
                if (parseInt(price) < this.minPrice) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.price.too_low', {min: this.minPrice}));
                }
            }
            return {errors: errors, warnings: warnings};
        },
        priceHasError() {
            return this.checkPrice().errors.length > 0;
        },
        priceHasWarning() {
            return this.checkPrice().warnings.length > 0;
        },
        isPriceValid() {
            return this.priceHasError() || this.priceHasWarning();
        },
        checkQuantity() {
            let errors = [];
            let warnings = [];

            if (this.$refs.quantity) {
                let quantity = this.$refs.quantity.value;

                if (quantity.length < 1) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.quantity.required'));
                }
                if (quantity.length > 0 && isNaN(parseInt(quantity))) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.quantity.number'));
                }
                if (parseInt(quantity) < this.minQuantity) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.quantity.too_low', {min: this.minQuantity}));
                }
                let potentialNumber = 0;
                let prevCount  = parseInt(this.form.quantity);
                for(let i=0; i<this.form.numberOfLevels; i++) {
                    potentialNumber += prevCount;
                    prevCount = parseInt(parseFloat(this.form.factor) * prevCount);
                }
                this.quantityTooHigh = potentialNumber > this.maxQuantity;
            }
            return {errors: errors, warnings: warnings};
        },
        quantityHasError() {
            return this.checkQuantity().errors.length > 0;
        },
        quantityHasWarning() {
            return this.checkQuantity().warnings.length > 0 || this.quantityTooHigh;
        },
        isQuantityValid() {
            return this.quantityHasError() || this.quantityHasWarning();
        },
        checkFactor() {
            let errors = [];
            let warnings = [];

            if (this.$refs.factor) {
                let factor = this.$refs.factor.value;

                if (factor.length < 1) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.factor.required'));
                }
                if (factor.length > 0 && isNaN(parseFloat(factor))) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.factor.number'));
                }
                if (parseFloat(factor) < this.minFactor) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.factor.too_low', {min: this.minFactor}));
                }
                if (parseFloat(factor) > this.maxFactor) {
                    errors.push(this.$t('shared.modals.adjust_price_drop_modal.form.factor.too_high', {max: this.maxFactor}));
                }
            }
            return {errors: errors, warnings: warnings};
        },
        factorHasError() {
            return this.checkFactor().errors.length > 0;
        },
        factorHasWarning() {
            return this.checkFactor().warnings.length > 0;
        },
        isFactorValid() {
            return this.factorHasError() || this.factorHasWarning();
        },
        isAdjustmentNeeded() {
            let retVal = false;
            if (this.newLevels) {
                this.newLevels.forEach((level) => {
                    if (level.adjusted === true || level.new_quantity <= 0) {
                        retVal = true;
                    }
                });
            }
            return retVal;
        },
        updateTimeRanges() {
            this.updatePriceDropTime();
            this.updateShowStart();
            this.updateShowEnd();
            this.isDefault = this.areSettingsDefault();
        },
        updatePriceDropTime() {
            this.hourRanges.priceDropTime = this.hourRangePriceDrop();
            this.minuteRanges.priceDropTime = this.minuteRangePriceDrop();
        },
        updateShowStart() {
            this.hourRanges.showStartTime = this.hourRangeShow('showStartTime');
            this.minuteRanges.showStartTime = this.minuteRangeShow('showStartTime');
        },
        updateShowEnd() {
            this.hourRanges.showEndTime = this.hourRangeShow('showEndTime');
            this.minuteRanges.showEndTime = this.minuteRangeShow('showEndTime');
        },
        hourRangePriceDrop() {
            // min value is either minTime or showStartTime
            let min = this.toTimeArray(this.form.show ? this.form.showStartTime : this.getMinTime(), this.offsetInSeconds);
            let max = this.toTimeArray(this.form.show ? this.form.showEndTime : this.getMaxTime(), -this.offsetInSeconds);
            return Array.from({ length: 1 + max[0] - min[0] }, (v, i) => i + min[0]);
        },
        minuteRangePriceDrop() {
            let min = this.toTimeArray(this.form.show ? this.form.showStartTime : this.getMinTime(), this.offsetInSeconds);
            let max = this.toTimeArray(this.form.show ? this.form.showEndTime : this.getMaxTime(), -this.offsetInSeconds);
            let val = this.toTimeArray(this.form.priceDropTime, 0);
            return this.getValidRanges(val, max, min);
        },
        toTimeArray(value, offsetInSeconds = 0) {
            value = moment.isMoment(value) ? moment(value) : moment(value, this.$t('locale.formatTimeTwelveHours'));
            let time = value.add(offsetInSeconds, 's');
            let retVal = time.format("HH:mm").split(':').map(v => parseInt(v));
            retVal.push(time);
            return retVal;
        },
        hourRangeShow(timeType) {
            let ranges = this.rangeTimesShow(timeType);
            return Array.from({ length: 1 + ranges.max[0] - ranges.min[0] }, (v, i) => i + ranges.min[0]);
        },
        minuteRangeShow(timeType) {
            let ranges = this.rangeTimesShow(timeType);
            return this.getValidRanges(this.toTimeArray(this.form[timeType]), ranges.max, ranges.min);
        },
        rangeTimesShow(timeType) {
            let min, max;
            if (timeType === 'showStartTime') {
                min = this.toTimeArray(this.getMinTime(), this.offsetInSeconds);
                max = this.toTimeArray(this.form.showEndTime, -this.offsetInSeconds);
            }
            if (timeType === 'showEndTime') {
                min = this.toTimeArray(this.form.showStartTime, this.offsetInSeconds);
                max = this.toTimeArray(this.getMaxTime(), -this.offsetInSeconds);
            }
            return {max, min};
        },
        getValidRanges(val, max, min) {
            if (val[0] > max[0] || val[0] < min[0] || min[0] > max[0]) {
                return []; // previous and next overlap already
            }
            if (val[0] === min[0] && val[0] === max[0]) { // both previous and next hour are the same limit in wetween
                return Array.from({length: 1 + max[1] - min[1]}, (v, i) => i + min[1]);
            } else if (val[0] === min[0]) { // same hour as previous limit above selection in previous item
                return Array.from({length: 60 - min[1]}, (v, i) => i + min[1]);
            } else if (val[0] === max[0]) { // same hour as next limit below selection in next item
                return Array.from({length: 1+ max[1]}, (v, i) => i);
            } else { // no limitations full hours minutes
                return Array.from({length: 60}, (v, i) => i);
            }
        },
        initRanges() {
            this.hourRanges = {
                priceDropTime:  Array.from({ length: 23 }, (v, i) => i),
                showStartTime:  Array.from({ length: 23 }, (v, i) => i),
                showEndTime:  Array.from({ length: 23 }, (v, i) => i),
            };
            this.minuteRanges = {
                priceDropTime:  Array.from({ length: 60 }, (v, i) => i),
                showStartTime:  Array.from({ length: 60 }, (v, i) => i),
                showEndTime:  Array.from({ length: 60 }, (v, i) => i),
            };
        },
        toTimeString(value) {
            return value.format(this.$t('locale.formatTimeTwelveHours'));
        },
        areSettingsDefault() {
            let priceDropTime = moment.tz(this.form.priceDropTime, this.$t('locale.formatTimeTwelveHours'), this.tz.auctionTimeZone()).format('HH:mm');
            let pdt = this.priceDropTimeSettings.find(date => date === priceDropTime);
            if (pdt) { // price drop is default
                if (this.form.show === true) {
                    let campaign = this.getCampaignByTime(this.priceDropTime);
                    if (campaign && campaign.date_start) {
                        let cs = moment.tz(campaign.date_start, this.tz.auctionTimeZone()).format(this.$t('locale.formatTimeTwelveHours'))
                        let ce = moment.tz(campaign.date_end, this.tz.auctionTimeZone()).format(this.$t('locale.formatTimeTwelveHours'))
                        if (cs === this.form.showStartTime && ce === this.form.showEndTime) {
                            return true;
                        }
                    }
                    return false;
                }
                return true;
            }
            return false;
        },
        getCampaignByTime(priceDropTime) {
            for (let i = 0; i < this.auction.campaigns.length; i++) {
                let start = moment.tz(this.auction.campaigns[i].date_start, this.tz.auctionTimeZone());
                let end = moment.tz(this.auction.campaigns[i].date_end, this.tz.auctionTimeZone());
                if (priceDropTime < end && priceDropTime > start) {
                    return this.auction.campaigns[i];
                }
            }
            return null;
        },
        getMinTime() {
           let min = moment(this.minTime, this.$t('locale.formatTimeTwelveHours'));
           let now = moment().tz(this.tz.auctionTimeZone());
           return moment.max(now, min);
        },
        getMaxTime() {
            let max = moment(this.maxTime, this.$t('locale.formatTimeTwelveHours'));
            let end = moment.tz(this.auction.date_end, this.tz.auctionTimeZone());
            return moment.min(max, end);
        }
    },
    computed: {
        tz() {
            return TimeZoneService;
        },
        filteredLevels() {
            let self = this;
            let retVal = [];
            let oldLevels = this.oldLevels.filter(lvl => lvl.phase === this.levelPhase);
            let newLevels = this.newLevels.filter(lvl => lvl.phase === this.levelPhase);
            let lvlCount = Math.max(oldLevels.length, newLevels.length);
            for( let i = 0; i < lvlCount; i++) {
                let lvl = {};
                // if old level exists
                if (oldLevels[i]) {
                    lvl.sequence = oldLevels[i].sequence;
                    lvl.price_drop = oldLevels[i].price_drop;
                    lvl.fixed_time = oldLevels[i].fixed_time;
                    lvl.price = oldLevels[i].price;
                    lvl.quantity = oldLevels[i].quantity;
                } else {
                    lvl.sequence = '';
                    lvl.price_drop = 0;
                    lvl.fixed_time = '';
                    lvl.price = null;
                    lvl.quantity = '';
                }

                // if new level exists
                if (newLevels[i]) {
                    lvl.new_sequence = newLevels[i].sequence;
                    lvl.new_price_drop = newLevels[i].price_drop;
                    lvl.new_fixed_time = newLevels[i].fixed_time;
                    lvl.new_price = newLevels[i].price;
                    lvl.new_quantity = newLevels[i].quantity;
                    lvl.adjusted = newLevels[i].adjusted;
                } else {
                    lvl.new_sequence = '';
                    lvl.new_price_drop = 0;
                    lvl.new_fixed_time = '';
                    lvl.new_price = null;
                    lvl.new_quantity = '';
                    lvl.adjusted = false;
                }
                retVal.push(lvl);
            }
            return retVal;
        }
    }
}
</script>
